import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { skillService } from '../../services/skillService';
import { goalService } from '../../services/goalService';
import { Skill, getSkillColor } from '../../types/Skill';
import { Goal } from '../../types/Goal';
import PageHeader from '../common/PageHeader';
import './SkillRoadmapYear.css';

interface SkillRoadmapYearProps {
  onToggleSidebar: () => void;
}

interface MonthData {
  month: number;
  monthName: string;
  isCurrent: boolean;
  isPast: boolean;
  isFuture: boolean;
  goals?: Goal[];
}

const SkillRoadmapYear: React.FC<SkillRoadmapYearProps> = ({ onToggleSidebar }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { year: yearParam } = useParams<{ year: string }>();
  const [skills, setSkills] = useState<Skill[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Validate and parse year parameter
  const getSelectedYear = (): number => {
    if (!yearParam) {
      return new Date().getFullYear();
    }
    const parsedYear = parseInt(yearParam, 10);
    if (isNaN(parsedYear) || parsedYear < 2000 || parsedYear > 2100) {
      return new Date().getFullYear();
    }
    return parsedYear;
  };

  const selectedYear = getSelectedYear();
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth();

  // Validate year parameter and redirect if invalid
  useEffect(() => {
    if (yearParam) {
      const parsedYear = parseInt(yearParam, 10);
      if (isNaN(parsedYear) || parsedYear < 2000 || parsedYear > 2100) {
        navigate(`/skillmap/${new Date().getFullYear()}`, { replace: true });
        return;
      }
    }
  }, [yearParam, navigate]);

  useEffect(() => {
    if (user) {
      loadSkills();
    }
  }, [user, selectedYear]);

  const loadSkills = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      const result = await skillService.getUserSkills(user.id.toString());
      
      if (result.success && result.skills) {
        // Convert linkedGoals from Firebase format to Goal format
        const convertedSkills: Skill[] = result.skills.map(skill => {
          const convertedLinkedGoals: Goal[] | undefined = skill.linkedGoals && Array.isArray(skill.linkedGoals)
            ? skill.linkedGoals.map((goal: any) => {
                try {
                  return goalService.convertFirebaseGoal(goal);
                } catch (err) {
                  console.warn('Error converting goal:', err, goal);
                  return null;
                }
              }).filter((g): g is Goal => g !== null)
            : undefined;
          
          return {
            ...skill,
            linkedGoals: convertedLinkedGoals
          } as Skill;
        });

        // Filter skills to only include those with goals in the selected year
        const filteredSkills = convertedSkills.filter(skill => {
          if (!skill.linkedGoals || skill.linkedGoals.length === 0) {
            return false;
          }

          // Check if skill has at least one goal in the selected year
          return skill.linkedGoals.some(goal => {
            const hasStartedDate = goal.startedDate && goal.startedDate.trim() !== '';
            const hasTargetDate = goal.targetDate && goal.targetDate.trim() !== '';
            
            if (!hasStartedDate || !hasTargetDate) {
              // Goals without dates are shown in all years
              return true;
            }

            try {
              const startedDate = new Date(goal.startedDate!);
              const targetDate = new Date(goal.targetDate!);
              
              if (isNaN(startedDate.getTime()) || isNaN(targetDate.getTime())) {
                return true; // Invalid dates, show in all years
              }

              const startYear = startedDate.getFullYear();
              const endYear = targetDate.getFullYear();
              
              return selectedYear >= startYear && selectedYear <= endYear;
            } catch (err) {
              return true; // Error parsing dates, show in all years
            }
          });
        });

        setSkills(filteredSkills);
      } else {
        setSkills([]);
      }
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to load skills');
      console.error('Error loading skills:', err);
    } finally {
      setLoading(false);
    }
  };

  // Convert hex color to HSL for sorting
  const hexToHsl = (hex: string): { h: number; s: number; l: number } => {
    hex = hex.replace('#', '');
    
    const r = parseInt(hex.substring(0, 2), 16) / 255;
    const g = parseInt(hex.substring(2, 4), 16) / 255;
    const b = parseInt(hex.substring(4, 6), 16) / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0;
    let s = 0;
    const l = (max + min) / 2;

    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      
      switch (max) {
        case r:
          h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
          break;
        case g:
          h = ((b - r) / d + 2) / 6;
          break;
        case b:
          h = ((r - g) / d + 4) / 6;
          break;
      }
    }

    return { h: h * 360, s, l };
  };

  // Sort skills by color
  const sortedSkills = [...skills].sort((a, b) => {
    const colorA = getSkillColor(a);
    const colorB = getSkillColor(b);

    if (!colorA && !colorB) return 0;
    if (!colorA) return 1;
    if (!colorB) return -1;

    const hslA = hexToHsl(colorA);
    const hslB = hexToHsl(colorB);

    if (Math.abs(hslA.h - hslB.h) > 0.01) {
      return hslA.h - hslB.h;
    }

    if (Math.abs(hslA.s - hslB.s) > 0.01) {
      return hslA.s - hslB.s;
    }

    return hslA.l - hslB.l;
  });

  // Get goals mapped by month for a skill within the selected year
  const getGoalsByMonth = (skill: Skill): { goalsByMonth: Map<number, Goal[]>; allMonthGoals: Goal[] } => {
    const goalsByMonth = new Map<number, Goal[]>();
    const allMonthGoals: Goal[] = [];
    
    if (!skill.linkedGoals || skill.linkedGoals.length === 0) {
      return { goalsByMonth, allMonthGoals };
    }

    skill.linkedGoals.forEach((goal) => {
      const hasStartedDate = goal.startedDate && goal.startedDate.trim() !== '';
      const hasTargetDate = goal.targetDate && goal.targetDate.trim() !== '';
      
      if (!hasStartedDate || !hasTargetDate) {
        allMonthGoals.push(goal);
        return;
      }

      try {
        const startedDate = new Date(goal.startedDate!);
        const targetDate = new Date(goal.targetDate!);
        
        if (isNaN(startedDate.getTime()) || isNaN(targetDate.getTime())) {
          allMonthGoals.push(goal);
          return;
        }

        const startYear = startedDate.getFullYear();
        const endYear = targetDate.getFullYear();
        const startMonth = startedDate.getMonth();
        const endMonth = targetDate.getMonth();

        // Only process if goal overlaps with selected year
        if (selectedYear >= startYear && selectedYear <= endYear) {
          // Determine the actual start and end months within the selected year
          let actualStartMonth = 0;
          let actualEndMonth = 11;

          if (startYear === selectedYear) {
            actualStartMonth = startMonth;
          }
          if (endYear === selectedYear) {
            actualEndMonth = endMonth;
          }

          // Map goal to all months between start and end (inclusive) within the selected year
          for (let month = actualStartMonth; month <= actualEndMonth; month++) {
            if (!goalsByMonth.has(month)) {
              goalsByMonth.set(month, []);
            }
            goalsByMonth.get(month)!.push(goal);
          }
        }
      } catch (err) {
        console.warn(`Invalid dates for goal ${goal.id}: startedDate=${goal.startedDate}, targetDate=${goal.targetDate}`, err);
        allMonthGoals.push(goal);
      }
    });

    return { goalsByMonth, allMonthGoals };
  };

  // Get month data for a skill
  const getMonthDataForSkill = (skill: Skill, month: number): MonthData => {
    const isCurrent = selectedYear === currentYear && month === currentMonth;
    const isPast = selectedYear < currentYear || (selectedYear === currentYear && month < currentMonth);
    const isFuture = selectedYear > currentYear || (selectedYear === currentYear && month > currentMonth);

    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    // Get goals for this month (both month-specific and all-month goals)
    const { goalsByMonth, allMonthGoals } = getGoalsByMonth(skill);
    const monthSpecificGoals = goalsByMonth.get(month) || [];
    
    // Combine month-specific goals with all-month goals
    const goalsForMonth = [...monthSpecificGoals, ...allMonthGoals];

    return {
      month,
      monthName: monthNames[month],
      isCurrent,
      isPast,
      isFuture,
      goals: goalsForMonth.length > 0 ? goalsForMonth : undefined
    };
  };

  // Generate array of 12 months
  const months = Array.from({ length: 12 }, (_, i) => i);

  const handleBack = () => {
    navigate('/skillmap');
  };

  const rightActions = (
    <button onClick={handleBack} className="skillroadmap-year-back-btn">
      ← Back
    </button>
  );

  if (!user) {
    return (
      <div className="skillroadmap-year-container">
        <PageHeader
          onToggleSidebar={onToggleSidebar}
          centerContent={<h1 className="skillroadmap-year-header-title">/skillmap/{selectedYear}</h1>}
          rightActions={rightActions}
        />
        <div className="skillroadmap-year-auth-required">
          <p>Please sign in to view skill roadmap.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="skillroadmap-year-container">
      <PageHeader
        onToggleSidebar={onToggleSidebar}
        centerContent={<h1 className="skillroadmap-year-header-title">/skillmap/{selectedYear}</h1>}
        rightActions={rightActions}
      />
      
      <div className="skillroadmap-year-content">
        {error && (
          <div className="skillroadmap-year-error-banner">
            <span>{error}</span>
            <button onClick={() => setError(null)}>×</button>
          </div>
        )}

        {loading ? (
          <div className="skillroadmap-year-loading">Loading skills...</div>
        ) : skills.length === 0 ? (
          <div className="skillroadmap-year-empty-state">
            <h3>No skills with goals in {selectedYear}</h3>
            <p>Skills without goals in this year are hidden.</p>
            <button onClick={handleBack} className="skillroadmap-year-back-btn">
              ← Back to Roadmap
            </button>
          </div>
        ) : (
          <div className="skillroadmap-year-timeline">
            {/* Header */}
            <div className="skillroadmap-year-header-row">
              <div className="skillroadmap-year-header-skill-column">Skill</div>
              <div className="skillroadmap-year-header-months">
                {months.map((month) => {
                  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                  const isCurrentMonth = selectedYear === currentYear && month === currentMonth;
                  return (
                    <div
                      key={month}
                      className={`skillroadmap-year-month-header ${isCurrentMonth ? 'current-month' : ''}`}
                    >
                      {monthNames[month]}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Skills Rows */}
            <div className="skillroadmap-year-skills">
              {sortedSkills.map((skill) => {
                const skillColor = getSkillColor(skill);
                const rowStyle: React.CSSProperties = {
                  backgroundColor: skillColor || '#ffffff',
                  ...(skillColor ? { borderLeft: `3px solid ${skillColor}` } : {})
                };
                
                return (
                  <div 
                    key={skill.id} 
                    className="skillroadmap-year-skill-row"
                    style={rowStyle}
                  >
                    <div 
                      className="skillroadmap-year-skill-name skillroadmap-year-skill-name-clickable"
                      onClick={() => navigate(`/skillmap/${skill.id}/edit`)}
                    >
                      {skill.name}
                    </div>
                    <div className="skillroadmap-year-skill-timeline">
                      {months.map((month) => {
                        const monthData = getMonthDataForSkill(skill, month);

                        return (
                          <div key={month} className="skillroadmap-year-month-column">
                            {monthData.goals && monthData.goals.length > 0 ? (
                              <div
                                className={`skillroadmap-year-milestone-node ${
                                  monthData.isPast
                                    ? 'past'
                                    : monthData.isCurrent
                                    ? 'current'
                                    : 'future'
                                }`}
                                title={`${skill.name} - ${monthData.monthName} ${selectedYear}`}
                              >
                                <div className="skillroadmap-year-goal-titles">
                                  {monthData.goals.map((goal) => (
                                    <div 
                                      key={goal.id} 
                                      className="skillroadmap-year-goal-title skillroadmap-year-goal-title-clickable"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        navigate(`/goals/${goal.id}`);
                                      }}
                                    >
                                      - {goal.title}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ) : (
                              <div className="skillroadmap-year-milestone-node planned" style={{ opacity: 0.3 }}>
                                <div className="skillroadmap-year-goal-title">—</div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SkillRoadmapYear;

